import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, FolderOpen, Calendar, Share2, Building2, Users } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import CreateProjectDialog from "@/components/Auth/Projects/CreateProjectDialog";
import type { Database } from "@/integrations/supabase/types";

type Project = Database['public']['Tables']['projects']['Row'];

interface ProjectOverviewPageProps {
  projects: Project[];
  sharedProjects: Project[];
  onSelectProject: (projectId: string) => void;
  onCreateProject?: (name: string) => Promise<void>;
  isLoading?: boolean;
}

const ProjectOverviewPage: React.FC<ProjectOverviewPageProps> = ({
  projects,
  sharedProjects,
  onSelectProject,
  onCreateProject,
  isLoading = false
}) => {
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const handleProjectClick = (projectId: string) => {
    onSelectProject(projectId);
  };

  const formatProjectDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { 
        addSuffix: true, 
        locale: sv 
      });
    } catch {
      return "Okänt datum";
    }
  };

  const ProjectCard: React.FC<{ 
    project: Project; 
    isShared?: boolean;
    className?: string;
  }> = ({ project, isShared = false, className = "" }) => (
    <Card 
      className={`group cursor-pointer transition-all duration-200 card-marine bg-marine-800 hover:bg-marine-700 border-marine-600 hover:border-marine-500 hover:shadow-xl hover:-translate-y-1 ${className}`}
      onClick={() => handleProjectClick(project.id)}
      onMouseEnter={() => setHoveredProject(project.id)}
      onMouseLeave={() => setHoveredProject(null)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg font-medium text-white group-hover:text-terracotta transition-colors line-clamp-1">
              {project.name}
            </CardTitle>
            {isShared && (
              <div className="flex items-center gap-1.5 mt-1">
                <Share2 className="h-3.5 w-3.5 text-terracotta-500" />
                <span className="text-xs text-terracotta-500 font-medium">Delat med dig</span>
              </div>
            )}
          </div>
          <FolderOpen className={`h-5 w-5 text-slate-300 transition-all duration-200 ${
            hoveredProject === project.id ? 'text-terracotta scale-110' : ''
          }`} />
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {project.description && (
          <p className="text-sm text-slate-200 mb-3 line-clamp-2">
            {project.description}
          </p>
        )}
        
        <div className="flex items-center gap-4 text-xs text-slate-300">
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <span>Skapat {formatProjectDate(project.created_at)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="text-center space-y-4">
          <div className="h-8 bg-muted rounded-lg w-64 mx-auto animate-pulse"></div>
          <div className="h-4 bg-muted rounded w-96 mx-auto animate-pulse"></div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="card-marine">
              <CardHeader>
                <div className="h-6 bg-muted rounded animate-pulse"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded animate-pulse"></div>
                  <div className="h-4 bg-muted rounded w-3/4 animate-pulse"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const totalProjects = projects.length + sharedProjects.length;

  if (totalProjects === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center space-y-6 animate-fade-in">
        <div className="text-center space-y-4 max-w-md">
          <div className="mx-auto w-16 h-16 glassmorphism bg-terracotta/20 border-terracotta/30 rounded-full flex items-center justify-center">
            <Building2 className="h-8 w-8 text-terracotta" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-slate-900">Välkommen till dina uppdrag</h2>
            <p className="text-slate-600">
              Skapa ditt första projekt för att börja organisera ditt arbete med riskbedömningar och mappar.
            </p>
          </div>
          {onCreateProject && (
            <Button onClick={() => setIsCreateDialogOpen(true)} size="lg" className="gap-2 bg-terracotta hover:bg-terracotta/90 text-white">
              <Plus className="h-4 w-4" />
              Skapa ditt första projekt
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Section */}
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center space-y-4 shadow-sm">
        <div className="space-y-2">
          <h1 className="text-4xl font-display font-bold text-terracotta-500">Mina uppdrag</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Välkommen till din projektöversikt. Här kan du hantera dina byggprojekt, 
            riskbedömningar och samarbeten på ett organiserat sätt. Klicka på ett projekt för att komma igång.
          </p>
        </div>
        
        {/* Statistics */}
        <div className="flex justify-center gap-8 text-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <Building2 className="h-4 w-4 text-terracotta" />
            <span className="text-slate-700">{projects.length} egna projekt</span>
          </div>
          {sharedProjects.length > 0 && (
            <div className="flex items-center gap-2 text-slate-600">
              <Users className="h-4 w-4 text-champagne" />
              <span className="text-slate-700">{sharedProjects.length} delade projekt</span>
            </div>
          )}
        </div>
      </div>

      {/* My Projects Section */}
      {projects.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-medium text-slate-900 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-terracotta" />
              Mina projekt
            </h2>
            {onCreateProject && (
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(true)} className="gap-2 border-slate-300 hover:border-slate-400 text-slate-700 hover:bg-slate-50">
                <Plus className="h-4 w-4" />
                Nytt projekt
              </Button>
            )}
          </div>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {projects.map((project) => (
              <ProjectCard 
                key={project.id} 
                project={project}
                className="hover:border-primary/30"
              />
            ))}
          </div>
        </section>
      )}

      {/* Shared Projects Section */}
      {sharedProjects.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-medium text-slate-900 flex items-center gap-2">
            <Share2 className="h-5 w-5 text-champagne" />
            Projekt delade med mig
          </h2>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sharedProjects.map((project) => (
              <ProjectCard 
                key={project.id} 
                project={project} 
                isShared={true}
                className="hover:border-primary/30"
              />
            ))}
          </div>
        </section>
      )}

      {/* Quick Actions */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 shadow-sm">
        <h3 className="text-lg font-medium text-slate-900">Snabba åtgärder</h3>
        <div className="flex flex-wrap gap-3">
          {onCreateProject && (
            <Button variant="secondary" onClick={() => setIsCreateDialogOpen(true)} className="gap-2 bg-terracotta hover:bg-terracotta/90 text-slate-900 border-none">
              <Plus className="h-4 w-4" />
              Skapa nytt projekt
            </Button>
          )}
        </div>
      </section>

      {/* Create Project Dialog */}
      {onCreateProject && (
        <CreateProjectDialog
          isOpen={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onCreateProject={onCreateProject}
        />
      )}
    </div>
  );
};

export default ProjectOverviewPage;